<template>
  <div class="relative">
    <multiselect
      v-model="selected"
      :options="sortedOptions"
      :hide-selected="true"
      label="name"
      @remove="handleRemove"
      track-by="id"
      placeholder="Seçilmedi"
      selectLabel="Seç"
      selectedLabel="Seçildi"
      deselectLabel="Seçimi Kaldır"
      openDirection="bottom"
      :disabled="disabled"
      :loading="isLoadingState || isCalculatingDistance"
      @search-change="debouncedSearch"
      :style="customStyle"
    >
      <!-- Custom option template with ReservationBadge -->
      <template #option="{ option }">
        <div class="flex items-center justify-between w-full gap-2 py-1">
          <div class="flex flex-col flex-1 overflow-hidden">
            <span class="font-semibold text-sm">{{ option.vehicle || option.plate || option.name }}</span>
          </div>
          <ReservationBadge
            v-if="option.hasReservation"
            :vehicle="option"
            :compact="true"
            class="flex-shrink-0"
          />
        </div>
      </template>

      <!-- Custom single label template -->
      <template #singleLabel="{ option }">
        <div class="flex items-center justify-between w-full gap-2">
          <div class="flex items-center gap-2 flex-1 overflow-hidden">
            <span class="font-semibold">{{ option.vehicle || option.plate || option.name }}</span>
          </div>
          <ReservationBadge
            v-if="option.hasReservation"
            :vehicle="option"
            :compact="true"
            class="flex-shrink-0"
          />
        </div>
      </template>
    </multiselect>

    <!-- Loading overlay - Mesafe hesaplanıyor göstergesi -->
    <div
      v-if="isCalculatingDistance"
      class="absolute top-0 right-0 mt-2 mr-2 z-50"
    >
      <div class="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded shadow-sm">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Mesafe hesaplanıyor...</span>
      </div>
    </div>
  </div>
</template>

<script>
//Npm
import similarity from "similarity";
import Multiselect from "vue-multiselect";
import moment from "moment";

// Components
import ReservationBadge from "@/components/general/vehicleTable/ReservationBadge.vue";

// Utils
import { recommendSingleVehicleForOrder } from "@/utils/recomendation/index";
import { getCustomers, getProducts } from "@/utils/detailData.js";
import { isSequential } from "@/utils/recomendation/index.js";
import { recommendSingleVehicleWithWorker } from "../../../../utils/useRecommendSingleVehicleWorker";

// Debounce utility
function debounce(fn, delay) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Önbellek nesneleri
const productValidityCache = new Map();
const dateCheckCache = new Map();

export default {
  components: {
    Multiselect,
    ReservationBadge,
  },
  name: "vehicle-input",
  model: {
    props: "value",
    event: "change",
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    optList: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    load: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      default: () => ({}),
    },
    sorted: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: null,
    },
    customers: {
      type: Array,
      default: null,
    },
    isBulk: {
      type: Boolean,
      default: false,
    },
    warningControl: {
      type: Boolean,
      default: false,
    },
    vehicleSelect: {
      type: Boolean,
      default: false,
    },
    selectedVehicles: {
      type: Array,
      default: () => [],
    },
    customStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selected: null,
      isSearch: false,
      allProducts: [],
      sortedOptionsData: [],
      isLoading: false,
      previousSelected: null,
      updatePending: false,
      lastUpdateTime: 0,
      searchText: "",
      calculatedProduct: null,
      optionsUpdateTimeout: null,
      isCalculatingDistance: false,
      fleetWarningShown: false
    };
  },
  computed: {
    // Birleştirilmiş yükleme durumu
    isLoadingState() {
      return this.load || this.isLoading;
    },
    
    // Sıralanmış seçenekler
    sortedOptions() {
      if (!this.isSearch && this.optList?.length > 0) {
        return this.sortedOptionsData.length > 0 ? this.sortedOptionsData : this.optList;
      }
      return this.optList;
    },
    
    // Sipariş koordinatları ve bilgileri
    orderData() {
      if (!this.order) return null;
      
      return {
        startLat: Number(this.order.outpointLat || this.order.fillingLatitude || 0),
        startLng: Number(this.order.outpointLng || this.order.fillingLongitude || 0),
        endLat: Number(this.order.targetPointLat || this.order.deliveryLatitude || 0),
        endLng: Number(this.order.targetPointLng || this.order.deliveryLongitude || 0),
        deliveryDate: this.order.deliveryDate || this.order.estimatedComplateDate,
        tonnage: parseFloat(this.order.tonnage) || 26,
        customer: this.order.customer,
        haveType: this.order.haveType,
        productName: this.order.productType,
        productId: this.order.productId,
      };
    },
    
    // Ürün ADR gerektiriyor mu?
    productRequiresADR() {
      if (!this.calculatedProduct) return false;
      return this.calculatedProduct.typeId === 2;
    },
    
    // Koşul: Sipariş küçük bir sipariş mi?
    isSmallOrder() {
      if (!this.order || !this.order.unitType) return false;
      return this.order.unitType === 'TON' 
        ? parseFloat(this.order.tonnage) < 31 
        : parseFloat(this.order.tonnage) < 2;
    }
  },
  created() {
    // Search debounce
    this.debouncedSearch = debounce(this.changeSearch, 250);
    
    // Bileşen oluşturulduğunda başlangıç işlemleri
    this.initializeComponent();
  },
  methods: {
    // Bileşen başlangıç işlemleri
    async initializeComponent() {
      // İlk değer ataması
      this.selected = this.value || null;
      
      // Ürünleri yükle
      if (this.products) {
        this.allProducts = this.products;
      } else {
        await this.handleGetProducts();
      }
      
      // Eğer sıralama gerekiyorsa
      if (this.sorted) {
        // Müşterileri yükle
        if (!this.customers || this.customers?.length === 0) {
          this.customers = await getCustomers(this.$store.state.userData.token);
        }
        
        // Sıralanmış seçenekleri hesapla
        this.updateSortedOptions();
      }
      
      // Ürün hesaplaması yap
      this.calculateProduct();
    },
    
    // Mevcut sipariş için ürün tipini hesapla ve önbellekle
    calculateProduct() {
      if (!this.order?.productType || !this.allProducts?.length) return null;
      
      const productType = this.order.productType.toString().toLocaleLowerCase().replace(/\s/g, "");
      
      // Önbellekte kontrol et
      if (productValidityCache.has(productType)) {
        this.calculatedProduct = productValidityCache.get(productType);
        return this.calculatedProduct;
      }
      
      // Hesapla ve önbellekle
      const foundProduct = this.allProducts.find((p) => {
        if (!p.name) return false;
        return similarity(
          p.name.toString().toLocaleLowerCase().replace(/\s/g, ""),
          productType
        ) >= 0.8;
      });
      
      productValidityCache.set(productType, foundProduct || null);
      this.calculatedProduct = foundProduct;
      return foundProduct;
    },
    
    // Sıralanmış seçenekleri güncelle - throttled
    async updateSortedOptions() {
      // Minimum güncelleme aralığı (1 saniye)
      const now = Date.now();
      const timeSinceLastUpdate = now - this.lastUpdateTime;
      
      if (timeSinceLastUpdate < 1000) {
        // Çok kısa süre içinde tekrar çağrıldıysa
        if (!this.updatePending) {
          this.updatePending = true;
          clearTimeout(this.optionsUpdateTimeout);
          this.optionsUpdateTimeout = setTimeout(() => {
            this.updatePending = false;
            this.updateSortedOptionsImpl();
          }, 1000 - timeSinceLastUpdate);
        }
        return;
      }
      
      // Normal güncelleme
      this.lastUpdateTime = now;
      this.updatePending = false;
      this.updateSortedOptionsImpl();
    },
    
    // Gerçek güncelleme implementasyonu
    async updateSortedOptionsImpl() {
      if (!this.sorted || this.isSearch || !this.optList?.length || !this.orderData) {
        this.sortedOptionsData = this.optList;
        return;
      }
      
      this.isLoading = true;
      
      try {
        const result = await recommendSingleVehicleWithWorker({
        order: this.orderData,
        vehicles: this.optList,
        customers: this.customers,
        products: this.allProducts,
        totalCount: 30,
        isBulk: this.isBulk || false,
        skipTheTonnage: false,
        setAvailable: false,
        blockedVehicles: this.selectedVehicles,
        isClose: false,
        token: this.$store.state.userData.token
      });

        this.sortedOptionsData = Array.isArray(result) ? result : [];
      } catch (error) {
        console.error("Error updating sorted options:", error);
        this.sortedOptionsData = this.optList;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Arama işlemi
    changeSearch(val) {
      this.searchText = val;
      this.isSearch = Boolean(val);
    },
    
    // Remove event handler
    handleRemove() {
      this.$emit("change", 0);
    },
    
    // Seçim işlemi
   handleSelection(val, notShowPopup = false) {
      if (!this.optList?.length || !val) return;

      const findItem = this.optList.find((item) => item?.id == val?.id);
      if (!findItem) return;

      const hasFleetConflict =
        findItem.fleetCustomers?.length &&
        !findItem.fleetCustomers?.some(
          (customer) => customer?.customerId === this.order?.customerId
        );

      // 🚨 UYARI sadece 1 kere çıksın
      if (
        hasFleetConflict &&
        !notShowPopup &&
        !this.fleetWarningShown
      ) {
        this.fleetWarningShown = true; // 🔑 artık bir daha çıkmayacak

        const customers = findItem?.fleetCustomers
          .map((customer) => customer.customerName)
          .join(", ");

        this.swalBox(
          "warning",
          "UYARI !",
          `Seçilen aracın ait olduğu filo başka müşteriye atanmış:${customers}. Devam etmek için DEVAM ET'e basınız?`,
          true,
          "Devam et"
        ).then((acc) => {
          if (acc.isConfirmed) {
            this.emitChanges(val, findItem);
          } else {
            this.selected = this.previousSelected;
            this.handleSelection(this.previousSelected, true);
          }
        });
      } else {
        this.emitChanges(val, findItem);
      }
    },
    
    // Değişiklikleri emit et
    emitChanges(val, findItem) {
      // Manuel seçimde yük uyumsuzluğu kontrolü ekle
      if (this.order && this.customers) {
        const { checkCargoCompatibility } = require('@/utils/recomendation/controller/checkCargoCompatibility');
        const vehicleLastProduct = val?.isSequentialTrue ? val?.productId : val?.lastProduct;

        const cargoCheck = checkCargoCompatibility(
          vehicleLastProduct,
          this.customers,
          this.order.customer
        );

        // Emit edilen value'ya uyumsuzluk bilgilerini ekle (sadece detay ekranında görünsün)
        if (!cargoCheck.isCompatible) {
          val.recomendation = val.recomendation || {};
          val.recomendation.isIncompatible = true;
          val.recomendation.incompatibilityReason = cargoCheck.reason;
          val.recomendation.rejectedProduct = cargoCheck.rejectedProduct;
        }
      }

      // Manuel seçimde gerçek mesafe hesapla (async - arka planda)
      if (this.order && val) {
        this.calculateRealDistanceAsync(val);
      }

      this.$emit("change", val);
      // Elle yazarak arama yapıldıysa isManualTyped: true olarak işaretle
      this.$emit("changeName", val.name, this.isSearch);
      this.$emit("changeDriverName", findItem.driverName);
      this.$emit("changeDorse", findItem.dorse);
    },
    
    // Ürünleri getir
    async handleGetProducts() {
      this.allProducts = await getProducts(this.$store.state.userData.token);
    },
    
    // Tarih kontrolü - önbellekli
    checkDate(date, cacheKey) {
      if (!date) return false;
      
      // Önbellekte kontrol et
      const cacheKeyFull = `${date}_${cacheKey}`;
      if (dateCheckCache.has(cacheKeyFull)) {
        return dateCheckCache.get(cacheKeyFull);
      }
      
      // Hesapla ve önbellekle
      const result = moment(date).diff(moment(), "days", 1) > 0;
      dateCheckCache.set(cacheKeyFull, result);
      
      return result;
    },
    
    // Araç ve dokuman doğrulama
    validateVehicleDocuments(val) {
      if (!val) return { isValid: true, message: "" };
      
      let diffInspectionDate = true,
        dorseAdrEndDate = true,
        vehicleAdrEndDate = true,
        vehicleInspectionEndDate = true,
        srcEndTimeBool = true;
      
      // Tarih kontrollerini yap - hepsini bir kerede
      if (val?.inspectionEndDate)
        diffInspectionDate = this.checkDate(val.inspectionEndDate, "inspection");
        
      if (val?.dorseAdrEndDate)
        dorseAdrEndDate = this.checkDate(val.dorseAdrEndDate, "dorseAdr");
        
      if (val?.vehicleAdrEndDate)
        vehicleAdrEndDate = this.checkDate(val.vehicleAdrEndDate, "vehicleAdr");
        
      if (!val.srcEndTime || val.srcEndTime === "null")
        srcEndTimeBool = false
      else srcEndTimeBool = this.checkDate(val.srcEndTime, "src");
      
      if (val?.vehicleInspectionEndDate)
        vehicleInspectionEndDate = this.checkDate(val.vehicleInspectionEndDate, "vehicleInspection");
      
      // Mesaj oluştur
      let warningMessage = "";
      
      if (!diffInspectionDate) {
        warningMessage += "Dorse muayene tarihi dolmuş. ";
      }
      
      if (!dorseAdrEndDate && this.productRequiresADR) {
        warningMessage += "Dorse ADR belgesi süresi dolmuş. ";
      }
      
      if (!srcEndTimeBool && this.productRequiresADR) {
        warningMessage += "SRC-5 belgesi süresi dolmuş. ";
      }
      
      if (!vehicleAdrEndDate && this.productRequiresADR) {
        warningMessage += "Araç ADR belgesi süresi dolmuş. ";
      }
      
      if (!vehicleInspectionEndDate && val.isRent !== 1) {
        warningMessage += "Araç muayene tarihi dolmuş. ";
      }
      
      return {
        isValid: !warningMessage,
        message: warningMessage
      };
    },
    
    // Sıralamalı mı kontrol et
    checkSequential(val) {
      if (!val || !this.order) return false;

      // Ardışık kontrol yap
      return isSequential(val, {
        ...this.order,
        orderNo: this.order.orderNo || this.order.no, // Explicitly pass orderNo
        startLat: this.order.outpointLat,
        startLng: this.order.outpointLng,
        endLat: this.order.targetPointLat,
        endLng: this.order.targetPointLng,
      });
    },
    
    // Araç uygunluğunu kontrol et
    checkVehicleAvailability(val) {
      if (!val) return true;

      const sequentialResult = this.checkSequential(val);

      val.isSequentialTrue = val?.orderNos?.length > 0 ? sequentialResult : false;

      // Rezervasyon kontrolü (eğer rezervasyon sistemi aktifse)
      if (val.hasReservation && val.allReservations && val.allReservations.length > 0) {
        // Sipariş zaman bilgileri
        let orderStartTime = null;
        if (this.order.deliveryDate) {
          // deliveryDate format kontrol et
          const deliveryTime = this.order.deliveryTime || '00:00:00';

          // Eğer deliveryDate zaten "T" içeriyorsa (ISO format), sadece onu kullan
          if (this.order.deliveryDate.includes('T')) {
            orderStartTime = this.order.deliveryDate.split('T')[0] + ' ' + deliveryTime;
          } else {
            // Yoksa direkt ekle
            orderStartTime = `${this.order.deliveryDate} ${deliveryTime}`;
          }
        }

        const orderEndTime = orderStartTime ?
          moment(orderStartTime).add(3, 'hours').format('YYYY-MM-DD HH:mm:ss') : null;

        if (orderStartTime && orderEndTime) {
          const { isVehicleAvailableForTimeSlot } = require('@/utils/recomendation/controller/isVehicleAvailableForTimeSlot');
          const isTimeSlotAvailable = isVehicleAvailableForTimeSlot(val, orderStartTime, orderEndTime);

          // Zaman slotu müsait ise direkt true dön
          if (isTimeSlotAvailable) {
            console.log('✅ Zaman slotu müsait:', val.vehicle, 'rezervasyon çakışması yok');
            return true; // Zaman müsait, araç kullanılabilir
          }

          // Rezervasyon çakışması var, sequential kontrolü yap
          if (sequentialResult && val.allReservations.length === 1) {
            console.log('✅ Sequential iş: Araç', val.vehicle, 'rezervasyon çakışmasına rağmen zincirleme iş olarak atanabilir');
            return true; // Sequential, izin ver
          }

          console.log('❌ Rezervasyon çakışması:', val.vehicle, 'başka bir işe atanmış ve zincirleme değil');
          return false; // Rezervasyon çakışması ve sequential değil
        }
      }

      // Normal kontroller
      const normalCheck = (
        (Array.isArray(val?.orderNos)
          ? val.orderNos.filter((r) => r != this.order.orderNo).length == 0
          : true) ||
        (val?.planning?.isStopped === 1 && Array.isArray(val?.orderNos) && val.orderNos.length === 1) ||
        sequentialResult
      );

      return normalCheck;
    },
    
    // Kontrol et - Kırkayak uyumluluğu
    checkKirkayakCompatibility(val) {
      if (!val || !this.order) return true;
      
      return !(val?.capacity / 1000 <= 22 && this.order?.tonnage > 21);
    },
    
    // Yük uyumluluğunu kontrol et
    checkLoadCompatibility(val, sequentialResult) {
      if (!val) return { isCompatible: true, message: "" };
      
      const lastProduct = val?.isSequentialTrue ? val?.productId : val?.lastProduct;
      const productTypeMismatch = this.order.productType !== lastProduct && lastProduct;
      
      if (!lastProduct || productTypeMismatch) {
        const productInfo = lastProduct ? `(${lastProduct})` : "";
        
        const infoStatus = lastProduct
          ? ((sequentialResult && val.orderNos.filter(v=>v!=this.order.orderNo).length>0)
            ? "uygun değil"
            : (val?.isDirtyForDriver == 0
              ? "uygun değil. Fakat Sürücü tarafından Aracın temiz olduğu Teyit edilmiştir"
              : "uygun değil"))
          : "bilinmiyor";
            
        return {
          isCompatible: false,
          message: `Aracın son yük bilgisi ${productInfo} ${infoStatus}!`
        };
      }
      
      return { isCompatible: true, message: "" };
    },
    
    // Onay mesajı göster
    async verifiedMessage(message) {
      return this.$swal.fire({
        title: "UYARI!",
        text: message,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Evet",
        cancelButtonText: "Hayır",
      });
    },
    
    // Uyarı kutusu göster
    swalBox(
      icon,
      title,
      text,
      showConfirmButton = true,
      confirmButtonText = ""
    ) {
      return this.$swal.fire({
        icon,
        title,
        text,
        showConfirmButton,
        confirmButtonText,
      });
    },
    
    // Toplu sipariş onayı
    showConfirm() {
      this.$swal({
        title: "UYARI",
        text: "BU SİPARİŞ KAYIT EDİLDİKTEN SONRA TOPLU PLANLAMA EKRANINDAN PLANLANABİLİR!",
        icon: "warning",
        confirmButtonText: "Tamam",
      });
    },

    // Manuel seçimde gerçek mesafe hesaplama (async - arka planda)
    async calculateRealDistanceAsync(vehicle) {
      // Eğer zaten hesaplanmışsa tekrar hesaplama
      if (vehicle._osrmCalculated) {
        return;
      }

      this.isCalculatingDistance = true;

      try {
        const { realRouteDistance } = require('@/utils/recomendation/realRouteDistance');

        const fromLocation = {
          lat: parseFloat(vehicle.latitude || 0),
          lng: parseFloat(vehicle.longitude || 0)
        };

        const orderLocation = {
          lat: parseFloat(
            this.order.outpointLat || this.order.fillingLatitude || 0
          ),
          lng: parseFloat(
            this.order.outpointLng || this.order.fillingLongitude || 0
          )
        };

        // Koordinat kontrolü
        if (!fromLocation.lat || !fromLocation.lng ||
            !orderLocation.lat || !orderLocation.lng) {
          return;
        }


        // OSRM ile gerçek mesafe hesapla
        const realDistance = await realRouteDistance(
          fromLocation,
          orderLocation,
          true  // useFallback = true
        );

        if (realDistance && realDistance !== Infinity) {
          // Araç objesini güncelle
          vehicle.recomendation = vehicle.recomendation || {};
          vehicle.recomendation.distancePriority = realDistance.toFixed(2);
          vehicle.distance = realDistance.toFixed(2);
          vehicle._osrmCalculated = true;

          // Parent component'i güncelle (reactive update için)
          const emitData = {
            vehicleId: vehicle.id,
            orderNo: this.order.orderNo,
            distance: realDistance.toFixed(2)
          };
          this.$emit('distance-calculated', emitData);
        }
      } catch (error) {
        // Hata durumunda sessizce devam et
      } finally {
        this.isCalculatingDistance = false;
      }
    }
  },
  watch: {
    // Değer değişimi
    value: {
      handler(val) {
        if (!this.selected && val) {
          this.selected = val;
        }
      },
      immediate: true
    },
    
    // Seçim değişimi - ayrı izleyici çünkü kompleks
    selected: {
      handler(val) {
        this.fleetWarningShown = false;
        // Değişim yoksa işleme devam etme
        if (val?.id === this.value?.id) return;
        
        // Önceki değeri kaydet
        this.previousSelected = this.optList.find(
          (item) => item?.id == this.value?.id
        );
        
        // Ürün tipini yeniden hesapla
        this.calculateProduct();
        
        // Araç uygunluğu kontrolü
        const sequentialResult = this.checkSequential(val);
        const isVehicleAvailable = this.checkVehicleAvailability(val);
        
        // Araç uygun değilse uyarı ver ve değeri sıfırla
        if (!isVehicleAvailable && this.isSearch) {
          if (val?.orderNo) {
            this.$emit('isVehicleConflictModalVisible', {
              show: true,
              order: this.order,
              vehicle: val
            });
          } else {
            this.swalBox(
              "warning",
              "UYARI!",
              "Bu araç, zaten başka bir işe atanmış olduğu için bu işe atanamaz!",
              false,
              this.$t("general.OkayTitle")
            );
          }
          // Önceki seçime geri dön
          this.selected = this.previousSelected;
          this.handleSelection(this.previousSelected, true);
          return;
        }
        
        // Kırkayak uyumluluğunu kontrol et
        if (!this.checkKirkayakCompatibility(val)) {
          this.verifiedMessage(
            "Sipariş Kırkayak için uygun değil yine de devam etmek istiyor musunuz ? "
          ).then((acc) => {
            if (!acc.isConfirmed) {
              this.selected = "";
              this.$emit("change", val);
              this.$emit("changeName", "");
              this.$emit("changeDriverName", "");
              this.$emit("changeDorse", "");
              return;
            }
          });
        }
        
        // Belge kontrolü
        const documentCheck = this.validateVehicleDocuments(val);
        
        // Yük uyumluluğu kontrolü
        const loadCompatibility = this.checkLoadCompatibility(val, sequentialResult);
        
        // Uyarı mesajları varsa birleştir
        let warningMessage = documentCheck.message;
        
        // Uyarıları göster
        if (!loadCompatibility.isCompatible) {
          this.$swal.fire({
            icon: "warning",
            title: "UYARI!",
            text: `${warningMessage} ${loadCompatibility.message}`,
            showConfirmButton: true,
            confirmButtonText: "Tamam",
          });
          
          this.handleSelection(val);
        }
        
        // Belge sorunları varsa uyar
        if (warningMessage) {
          this.$swal
            .fire({
              icon: "warning",
              title: "UYARI!",
              text: warningMessage + " Lütfen belgelerin geçerliliğini kontrol edin.",
              showConfirmButton: true,
              confirmButtonText: "Tamam",
            })
            .then(() => {
              this.selected = this.previousSelected;
              this.handleSelection(this.previousSelected, true);
            });
          return;
        } else {
          this.handleSelection(val);
          return;
        }
      },
      deep: true
    },
    
    // Seçenek listesi değişirse
    optList: {
      handler() {
        this.updateSortedOptions();
      },
      deep: false
    },
    
    // Sipariş değişirse
    order: {
      handler() {
        this.updateSortedOptions();
        this.calculateProduct();
      },
      deep: true
    },
    
    // Seçilen araçlar değişirse
    selectedVehicles: {
      handler() {
        this.updateSortedOptions();
      },
      deep: false
    }
  },
  // Component yok edildiğinde timeout'ları temizle
  beforeDestroy() {
    clearTimeout(this.optionsUpdateTimeout);
  }
};
</script>

<style scoped>
/* Multiselect özelleştirme */
::v-deep .multiselect__option {
  padding: 8px 12px;
  min-height: 40px;
}

::v-deep .multiselect__option--highlight {
  background: #3b82f6;
}

::v-deep .multiselect__option--selected {
  background: #e0f2fe;
  color: #0c4a6e;
  font-weight: 600;
}

::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #0ea5e9;
  color: white;
}

/* Seçili değer için daha iyi görünüm */
::v-deep .multiselect__single {
  padding: 4px 0;
  margin-bottom: 0;
}

/* min-w-0 ile truncate'in çalışmasını sağla */
.min-w-0 {
  min-width: 0;
}
</style>